import * as React from "react";
import { Stack, Box, Typography } from "@mui/material";
import { StatusCard } from "@/components/dashboard/StatusCard";
import { PositionBadge } from "@/components/dashboard/PositionBadge";
import { EmptyState } from "@/components/dashboard/EmptyState";
import { tallyBy } from "@/utils/tally";
import type { OrderRow } from "./OrderTable";

interface OrderStatusPanelProps {
  orders: OrderRow[];
}

/**
 * The backend's `ExecutionOrderDto.status` field is `ExecutionStatus`
 * (PENDING/SUBMITTED/ACCEPTED/REJECTED/COMPLETED) - a narrower vocabulary
 * than the richer `OrderStatus` enum (ACKNOWLEDGED/PARTIALLY_FILLED/
 * FILLED/CANCELLED/EXPIRED) this section's brief assumed. Rather than
 * hardcode 6 buckets and show a fabricated "0" for statuses that cannot
 * structurally appear at this endpoint, this panel tallies whichever
 * status values are genuinely present in the fetched order book.
 */
export function OrderStatusPanel({ orders }: OrderStatusPanelProps): React.JSX.Element {
  const byStatus = tallyBy(orders, (o) => o.status);
  const entries = Object.entries(byStatus).sort(([a], [b]) => a.localeCompare(b));

  return (
    <StatusCard title="Order Status">
      {entries.length === 0 ? (
        <EmptyState message="No orders reported by the backend." />
      ) : (
        <Stack spacing={1.5}>
          {entries.map(([status, count]) => (
            <Box key={status} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <PositionBadge status={status} />
              <Typography variant="body2">{count}</Typography>
            </Box>
          ))}
        </Stack>
      )}
    </StatusCard>
  );
}
